import { useRef, useEffect, useState } from "react";
import { Check } from "lucide-react";

// Caselle OTP animate: auto-avanzamento, incolla, bagliore blu al riempimento,
// rotazione dei numeri in cerchio + conferma finale pulsante in caso di successo,
// shake in caso di errore.
export const OtpInput = ({ length = 6, value = "", onChange, onComplete, disabled = false, status = "idle" }) => {
  const refs = useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] || "");
  const [phase, setPhase] = useState("row"); // row -> circle -> rotate -> confirmed

  useEffect(() => {
    if (status === "error" && refs.current[0]) {
      // riporta il focus alla prima casella dopo un errore
      const t = setTimeout(() => refs.current[0]?.focus(), 350);
      return () => clearTimeout(t);
    }
  }, [status]);

  useEffect(() => {
    if (status !== "success") { setPhase("row"); return; }
    // 1) numeri allineati in riga → 2) si dispongono in cerchio → 3) ruotano → 4) conferma
    setPhase("row");
    const t1 = setTimeout(() => setPhase("circle"), 450);
    const t2 = setTimeout(() => setPhase("rotate"), 1150);
    const t3 = setTimeout(() => setPhase("confirmed"), 2900);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [status]);

  const setChar = (idx, char) => {
    const next = digits.slice();
    next[idx] = char;
    const joined = next.join("").slice(0, length);
    onChange?.(joined);
    return joined;
  };

  const handleChange = (idx, raw) => {
    const clean = raw.replace(/\D/g, "");
    if (!clean) { setChar(idx, ""); return; }
    // supporto incolla di più cifre in una singola casella
    if (clean.length > 1) {
      const arr = digits.slice();
      let p = idx;
      for (const ch of clean) { if (p >= length) break; arr[p] = ch; p += 1; }
      const joined = arr.join("").slice(0, length);
      onChange?.(joined);
      const focusAt = Math.min(p, length - 1);
      refs.current[focusAt]?.focus();
      if (joined.length === length) onComplete?.(joined);
      return;
    }
    const joined = setChar(idx, clean);
    if (idx < length - 1) refs.current[idx + 1]?.focus();
    if (joined.length === length) onComplete?.(joined);
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace") {
      if (digits[idx]) { setChar(idx, ""); return; }
      if (idx > 0) { refs.current[idx - 1]?.focus(); setChar(idx - 1, ""); }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      refs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < length - 1) {
      refs.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, length);
    if (!text) return;
    e.preventDefault();
    onChange?.(text);
    const focusAt = Math.min(text.length, length - 1);
    refs.current[focusAt]?.focus();
    if (text.length === length) onComplete?.(text);
  };

  return (
    <div className="flex justify-center" data-testid="otp-input" role="group" aria-label="Codice di verifica">
      {status === "success" ? (
        <div className={`otp-orbit ${phase === "row" ? "is-row" : ""} ${phase === "confirmed" ? "is-confirmed" : ""}`} data-testid="otp-orbit">
          <span className="otp-orbit-ring" />
          {phase === "confirmed" ? (
            <span className="otp-orbit-check" data-testid="otp-orbit-check">
              <Check className="h-8 w-8" strokeWidth={3} />
            </span>
          ) : phase !== "row" ? (
            <span className="otp-orbit-center" />
          ) : null}
          <div className={`otp-orbit-disc ${phase === "rotate" ? "is-rotate" : ""}`}>
            {digits.map((d, i) => {
              const angle = (i * 360) / length;
              const rowX = (i - (length - 1) / 2) * 52;
              const style = phase === "row"
                ? { transform: `translate(${rowX}px, 0)` }
                : { transform: `rotate(${angle}deg) translateY(-66px)` };
              return (
                <div key={i} className="otp-orbit-item" style={style}>
                  <div className="otp-cell otp-cell-success" data-testid={`otp-orbit-cell-${i}`}>{d}</div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex justify-center gap-2 sm:gap-2.5">
          {digits.map((d, i) => {
            const filled = d !== "";
            const cls = status === "error" ? "otp-cell-error" : filled ? "otp-cell-filled" : "otp-cell-empty";
            return (
              <input
                key={i}
                ref={(el) => (refs.current[i] = el)}
                data-testid={`otp-cell-${i}`}
                type="text"
                inputMode="numeric"
                autoComplete={i === 0 ? "one-time-code" : "off"}
                maxLength={length}
                value={d}
                disabled={disabled}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={handlePaste}
                onFocus={(e) => e.target.select()}
                className={`otp-cell ${cls}`}
                aria-label={`Cifra ${i + 1}`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OtpInput;
